import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Eye, CheckCircle, DollarSign, FileText, Clock, AlertTriangle, TrendingUp, CreditCard } from 'lucide-react';
import api from '../utils/api';
import { formatIndianDate } from '../utils/formatters';

const InvoiceManager = () => {
  const [invoices, setInvoices] = useState([]);
  const [pendingServices, setPendingServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    pending_count: 0,
    paid_count: 0,
    pending_amount: 0,
    paid_amount: 0
  });
  const [activeTab, setActiveTab] = useState('pending');
  
  // Dialog states
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false);
  const [isPaymentDialogOpen, setIsPaymentDialogOpen] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form states
  const [invoiceForm, setInvoiceForm] = useState({
    invoice_amount: '',
    due_date: '',
    notes: ''
  });
  const [paymentForm, setPaymentForm] = useState({
    payment_mode: '',
    payment_reference: '',
    payment_date: ''
  });

  const fetchInvoices = useCallback(async () => {
    try {
      const response = await api.get('/invoices/');
      setInvoices(response.data);
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/invoices/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  const fetchPendingServices = useCallback(async () => {
    try {
      // Get services that are approved and have Payment Pending status but no invoice yet
      const response = await api.get('/services/', {
        params: { status: 'Closed' }
      });
      // Filter for services without invoices that need invoicing
      const servicesToInvoice = response.data.filter(s => 
        s.approval_status === 'Approved' && 
        s.payment_status === 'Payment Pending' &&
        !s.invoice_id
      );
      setPendingServices(servicesToInvoice);
    } catch (error) {
      console.error('Error fetching pending services:', error);
    }
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchInvoices(), fetchStats(), fetchPendingServices()]);
      setLoading(false);
    };
    fetchData();
  }, [fetchInvoices, fetchStats, fetchPendingServices]);

  const handleOpenCreateDialog = (service) => {
    setSelectedService(service);
    setInvoiceForm({
      invoice_amount: service.service_charge || '',
      due_date: '',
      notes: ''
    });
    setError('');
    setIsCreateDialogOpen(true);
  };

  const handleCreateInvoice = async () => {
    if (!invoiceForm.invoice_amount || !invoiceForm.due_date) {
      setError('Amount and Due Date are required');
      return;
    }

    try {
      const response = await api.post('/invoices/', null, {
        params: {
          service_id: selectedService.id,
          invoice_amount: parseFloat(invoiceForm.invoice_amount),
          due_date: invoiceForm.due_date,
          notes: invoiceForm.notes || null
        }
      });
      setSuccess(`Invoice ${response.data.invoice_no} created successfully`);
      setIsCreateDialogOpen(false);
      fetchInvoices();
      fetchStats();
      fetchPendingServices();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error creating invoice');
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setIsViewDialogOpen(true);
  };

  const handleOpenPaymentDialog = (invoice) => {
    setSelectedInvoice(invoice);
    setPaymentForm({
      payment_mode: '',
      payment_reference: '',
      payment_date: new Date().toISOString().split('T')[0]
    });
    setError('');
    setIsPaymentDialogOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!paymentForm.payment_mode) {
      setError('Payment mode is required');
      return;
    }

    try {
      await api.post(`/invoices/${selectedInvoice.id}/mark-paid`, null, {
        params: {
          payment_mode: paymentForm.payment_mode,
          payment_reference: paymentForm.payment_reference || null,
          payment_date: paymentForm.payment_date || null
        }
      });
      setSuccess('Invoice marked as paid successfully');
      setIsPaymentDialogOpen(false);
      fetchInvoices();
      fetchStats();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Error updating invoice');
    }
  };

  const pendingInvoices = invoices.filter(inv => inv.payment_status === 'Pending');
  const paidInvoices = invoices.filter(inv => inv.payment_status === 'Paid');

  const getStatusBadge = (status) => {
    switch (status) {
      case 'Paid':
        return <Badge className="bg-green-100 text-green-800">Paid</Badge>;
      case 'Pending':
        return <Badge className="bg-yellow-100 text-yellow-800">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Invoice Manager</h1>
          <p className="text-gray-600 mt-1">Manage service invoices and payments</p>
        </div>
      </div>

      {/* Alerts */}
      {success && (
        <Alert className="bg-green-50 border-green-200">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription className="text-green-700">{success}</AlertDescription>
        </Alert>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">Total Invoices</p>
                <p className="text-2xl font-bold text-blue-800">{stats.total}</p>
              </div>
              <FileText className="h-10 w-10 text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-yellow-600">Pending</p>
                <p className="text-2xl font-bold text-yellow-800">{stats.pending_count}</p>
                <p className="text-xs text-yellow-600">₹{stats.pending_amount?.toLocaleString('en-IN')}</p>
              </div>
              <Clock className="h-10 w-10 text-yellow-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">Paid</p>
                <p className="text-2xl font-bold text-green-800">{stats.paid_count}</p>
                <p className="text-xs text-green-600">₹{stats.paid_amount?.toLocaleString('en-IN')}</p>
              </div>
              <TrendingUp className="h-10 w-10 text-green-400" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-purple-600">Awaiting Invoice</p>
                <p className="text-2xl font-bold text-purple-800">{pendingServices.length}</p>
                <p className="text-xs text-purple-600">Services</p>
              </div>
              <AlertTriangle className="h-10 w-10 text-purple-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pending" className="flex items-center gap-2">
            <Clock size={16} />
            Pending Payment ({pendingInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="paid" className="flex items-center gap-2">
            <CheckCircle size={16} />
            Paid ({paidInvoices.length})
          </TabsTrigger>
          <TabsTrigger value="create" className="flex items-center gap-2">
            <DollarSign size={16} />
            Create Invoice ({pendingServices.length})
          </TabsTrigger>
        </TabsList>

        {/* Pending Invoices Tab */}
        <TabsContent value="pending">
          <Card>
            <CardHeader>
              <CardTitle>Pending Invoices</CardTitle>
              <CardDescription>Invoices awaiting payment</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingInvoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
                  <p>No pending invoices</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Due Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingInvoices.map((invoice) => (
                      <TableRow key={invoice.id}>
                        <TableCell className="font-medium">{invoice.invoice_no}</TableCell>
                        <TableCell>{invoice.service?.service_no || 'N/A'}</TableCell>
                        <TableCell>{invoice.customer?.company_name || 'N/A'}</TableCell>
                        <TableCell className="font-semibold">
                          ₹{invoice.invoice_amount?.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>{formatIndianDate(invoice.due_date)}</TableCell>
                        <TableCell>{getStatusBadge(invoice.payment_status)}</TableCell>
                        <TableCell className="text-right space-x-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            <Eye size={16} />
                          </Button>
                          <Button 
                            variant="default" 
                            size="sm"
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => handleOpenPaymentDialog(invoice)}
                          >
                            <CreditCard size={16} className="mr-1" />
                            Mark Paid
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Paid Invoices Tab */}
        <TabsContent value="paid">
          <Card>
            <CardHeader>
              <CardTitle>Paid Invoices</CardTitle>
              <CardDescription>Completed payment records</CardDescription>
            </CardHeader>
            <CardContent>
              {paidInvoices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <FileText size={48} className="mx-auto mb-4 text-gray-300" />
                  <p>No paid invoices yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice No.</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Payment Date</TableHead>
                      <TableHead>Payment Mode</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {paidInvoices.map((invoice) => (
                      <TableRow key={invoice.id} className="bg-green-50">
                        <TableCell className="font-medium">{invoice.invoice_no}</TableCell>
                        <TableCell>{invoice.service?.service_no || 'N/A'}</TableCell>
                        <TableCell>{invoice.customer?.company_name || 'N/A'}</TableCell>
                        <TableCell className="font-semibold text-green-700">
                          ₹{invoice.invoice_amount?.toLocaleString('en-IN')}
                        </TableCell>
                        <TableCell>{formatIndianDate(invoice.payment_date)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{invoice.payment_mode}</Badge>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleViewInvoice(invoice)}
                          >
                            <Eye size={16} />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Create Invoice Tab */}
        <TabsContent value="create">
          <Card>
            <CardHeader>
              <CardTitle>Services Awaiting Invoice</CardTitle>
              <CardDescription>Approved services with payment pending - create invoices</CardDescription>
            </CardHeader>
            <CardContent>
              {pendingServices.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle size={48} className="mx-auto mb-4 text-green-300" />
                  <p>No services awaiting invoice</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Service No.</TableHead>
                      <TableHead>Customer</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Service Charge</TableHead>
                      <TableHead>Completed On</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pendingServices.map((service) => (
                      <TableRow key={service.id} className="bg-purple-50">
                        <TableCell className="font-medium">{service.service_no}</TableCell>
                        <TableCell>{service.customer?.company_name || 'N/A'}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{service.service_type}</Badge>
                        </TableCell>
                        <TableCell className="font-semibold">
                          {service.service_charge ? `₹${service.service_charge.toLocaleString('en-IN')}` : '-'}
                        </TableCell>
                        <TableCell>{formatIndianDate(service.updated_at)}</TableCell>
                        <TableCell className="text-right">
                          <Button 
                            variant="default" 
                            size="sm"
                            onClick={() => handleOpenCreateDialog(service)}
                          >
                            <DollarSign size={16} className="mr-1" />
                            Create Invoice
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Create Invoice Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create Invoice</DialogTitle>
            <DialogDescription>
              Create invoice for service {selectedService?.service_no}
            </DialogDescription>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Customer</p>
              <p className="font-medium">{selectedService?.customer?.company_name}</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="invoice_amount">Invoice Amount (₹) *</Label>
              <Input
                id="invoice_amount"
                type="number"
                value={invoiceForm.invoice_amount}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, invoice_amount: e.target.value }))}
                placeholder="Enter amount"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="due_date">Due Date *</Label>
              <Input
                id="due_date"
                type="date"
                value={invoiceForm.due_date}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, due_date: e.target.value }))}
                min={new Date().toISOString().split('T')[0]}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={invoiceForm.notes}
                onChange={(e) => setInvoiceForm(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Optional notes..."
                rows={3}
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateInvoice}>
                Create Invoice
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* View Invoice Dialog */}
      <Dialog open={isViewDialogOpen} onOpenChange={setIsViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Invoice Details</DialogTitle>
            <DialogDescription>{selectedInvoice?.invoice_no}</DialogDescription>
          </DialogHeader>
          
          {selectedInvoice && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Customer</p>
                  <p className="font-medium">{selectedInvoice.customer?.company_name}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Service</p>
                  <p className="font-medium">{selectedInvoice.service?.service_no}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded-lg">
                  <p className="text-sm text-blue-600">Amount</p>
                  <p className="font-bold text-xl text-blue-800">
                    ₹{selectedInvoice.invoice_amount?.toLocaleString('en-IN')}
                  </p>
                </div>
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Due Date</p>
                  <p className="font-medium">{formatIndianDate(selectedInvoice.due_date)}</p>
                </div>
              </div>

              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Status</p>
                <div className="mt-1">{getStatusBadge(selectedInvoice.payment_status)}</div>
              </div>

              {selectedInvoice.payment_status === 'Paid' && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-600">Payment Date</p>
                    <p className="font-medium">{formatIndianDate(selectedInvoice.payment_date)}</p>
                  </div>
                  <div className="bg-green-50 p-3 rounded-lg">
                    <p className="text-sm text-green-600">Payment Mode</p>
                    <p className="font-medium">{selectedInvoice.payment_mode}</p>
                  </div>
                  {selectedInvoice.payment_reference && (
                    <div className="col-span-2 bg-green-50 p-3 rounded-lg">
                      <p className="text-sm text-green-600">Reference</p>
                      <p className="font-medium">{selectedInvoice.payment_reference}</p>
                    </div>
                  )}
                </div>
              )}

              {selectedInvoice.notes && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="text-sm text-gray-600">Notes</p>
                  <p className="font-medium">{selectedInvoice.notes}</p>
                </div>
              )}

              <div className="text-xs text-gray-500">
                Created: {formatIndianDate(selectedInvoice.created_at)}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark Paid Dialog */}
      <Dialog open={isPaymentDialogOpen} onOpenChange={setIsPaymentDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
            <DialogDescription>
              Record payment for {selectedInvoice?.invoice_no}
            </DialogDescription>
          </DialogHeader>
          
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <p className="text-sm text-blue-600">Amount</p>
              <p className="font-bold text-xl text-blue-800">
                ₹{selectedInvoice?.invoice_amount?.toLocaleString('en-IN')}
              </p>
            </div>

            <div className="grid gap-2">
              <Label>Payment Mode *</Label>
              <Select 
                value={paymentForm.payment_mode} 
                onValueChange={(value) => setPaymentForm(prev => ({ ...prev, payment_mode: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select payment mode" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="UPI">UPI</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="payment_date">Payment Date</Label>
              <Input
                id="payment_date"
                type="date"
                value={paymentForm.payment_date}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_date: e.target.value }))}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="payment_reference">Reference Number</Label>
              <Input
                id="payment_reference"
                value={paymentForm.payment_reference}
                onChange={(e) => setPaymentForm(prev => ({ ...prev, payment_reference: e.target.value }))}
                placeholder="Transaction ID, Cheque No., etc."
              />
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setIsPaymentDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleMarkPaid} className="bg-green-600 hover:bg-green-700">
                <CheckCircle size={16} className="mr-1" />
                Confirm Payment
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default InvoiceManager;
